<template>
  <ModalWindow :open="open" @toggle-modal="emit('close')">
    <div class="flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <h2 class="text-text-primary font-semibold text-xl">{{ $t('play.matchmaking.invite_friends') }}</h2>
        <div class="flex items-center gap-2">
          <button
            @click="fetchFriends"
            :disabled="loading"
            class="text-gray-400 hover:text-gray-200 disabled:opacity-50"
            :title="$t('play.matchmaking.refresh')"
          >
            <ArrowPathIcon class="w-5 h-5" :class="{ 'animate-spin': loading }" />
          </button>
          <button @click="emit('close')" class="text-gray-400 hover:text-gray-200">
            <XMarkIcon class="w-5 h-5" />
          </button>
        </div>
      </div>

      <p v-if="loading && !loaded" class="text-gray-500 text-sm text-center py-4">{{ $t('play.matchmaking.loading_friends') }}</p>
      <p v-else-if="loadError" class="text-red-400 text-sm text-center py-4">{{ loadError }}</p>
      <p v-else-if="!onlineFriends.length" class="text-gray-500 text-sm text-center py-4">{{ $t('play.matchmaking.no_friends_online') }}</p>

      <div v-else class="flex flex-col gap-1 max-h-96 overflow-y-auto -mx-2">
        <div
          v-for="friend in onlineFriends"
          :key="friend.uuid"
          class="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-800/60 transition-colors"
        >
          <UserAvatar :user="friend" :online="true" />
          <span class="text-text-primary text-sm truncate flex-1">{{ friend.display_name }}</span>
          <span v-if="isInParty(friend)" class="text-xs text-gray-400">{{ $t('play.matchmaking.in_party') }}</span>
          <span v-else-if="invitedUuids.has(friend.uuid)" class="text-xs text-primary-400">{{ $t('play.matchmaking.invited') }}</span>
          <button
            v-else
            @click="sendInvite(friend)"
            :disabled="!party?.id || sendingUuids.has(friend.uuid)"
            class="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-500 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
          >
            <UserPlusIcon class="w-3.5 h-3.5" />
            {{ $t('play.matchmaking.invite') }}
          </button>
        </div>
      </div>

      <p v-if="inviteError" class="text-red-400 text-xs">{{ inviteError }}</p>
    </div>
  </ModalWindow>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { ArrowPathIcon, UserPlusIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import ModalWindow from '../ModalWindow.vue';
import UserAvatar from '../layouts/UserAvatar.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
});
const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const friends = ref([]);
const loading = ref(false);
const loaded = ref(false);
const loadError = ref('');
const invitedUuids = ref(new Set());
const sendingUuids = ref(new Set());
const inviteError = ref('');
let fetchSeq = 0;

const party = computed(() => store.getters.matchmakingParty);
const friendPresence = computed(() => store.getters.friendPresence);

const onlineFriends = computed(() => friends.value.filter((friend) => {
  const override = friendPresence.value[friend.uuid];
  return override !== undefined ? override : !!friend.online;
}));

function isInParty(friend) {
  return (party.value?.members || []).some((member) => member.uuid === friend.uuid);
}

function fetchFriends() {
  const seq = ++fetchSeq;
  const revisionAtRequest = store.getters.friendPresenceRevision;
  loading.value = true;
  loadError.value = '';
  axios
    .get('/friendships')
    .then(({ data }) => {
      if (seq !== fetchSeq) return;
      friends.value = data.filter((f) => f.status === 'accepted').map((f) => f.friend);
      store.commit(
        'mergeFriendPresence',
        Object.fromEntries(
          friends.value
            .filter((friend) => (store.getters.friendPresenceRevisions[friend.uuid] ?? 0) <= revisionAtRequest)
            .map((friend) => [friend.uuid, !!friend.online]),
        ),
      );
      loaded.value = true;
    })
    .catch(() => {
      if (seq === fetchSeq) loadError.value = t('play.matchmaking.load_friends_failed');
    })
    .finally(() => {
      if (seq === fetchSeq) loading.value = false;
    });
}

function sendInvite(friend) {
  if (!party.value?.id) return;
  inviteError.value = '';
  sendingUuids.value = new Set([...sendingUuids.value, friend.uuid]);
  axios
    .post(`/matchmaking/parties/${party.value.id}/party_invites`, { invitee_uuid: friend.uuid })
    .then(() => {
      invitedUuids.value = new Set([...invitedUuids.value, friend.uuid]);
    })
    .catch((err) => {
      inviteError.value = err.response?.data?.errors?.join(', ') || t('play.matchmaking.invite_failed');
    })
    .finally(() => {
      const next = new Set(sendingUuids.value);
      next.delete(friend.uuid);
      sendingUuids.value = next;
    });
}

watch(
  () => props.open,
  (open) => {
    if (!open) return;
    inviteError.value = '';
    invitedUuids.value = new Set();
    fetchFriends();
  },
  { immediate: true },
);
</script>
